import React, {useEffect} from 'react';
import {ButtonMUI, DialogMUI, InputMUI} from '../../../shared';
import {useDispatch, useSelector} from 'react-redux';
import {closeDictionariesEdit, resetAllDictionariesEdit} from '../../../redux/actions/dictionaries/dictionariesActions';
import * as yup from 'yup';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {editCity, editCountry, getCities, getCountries} from '../../../redux/actions/countries/countriesActions';
import {editProjectType, getProjectTypes} from '../../../redux/actions/projects/projectsActions';
import {notifySuccess} from '../../../helpers/notifySnack';
import {useTranslation} from 'react-i18next';

const UserDictionariesPopup = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const closePopup = () => {
    dispatch(closeDictionariesEdit());
    setTimeout(() => {
      dispatch(resetAllDictionariesEdit());
    }, 150);
  };

  const popupData = useSelector((state) => state.dictionaries.editPopup);

  const schema = yup.object().shape({
    type: yup.number().required(t('validations.required')),
    country: yup.string().when('type', {
      is: (val) => val === 1, // Якщо обрано тип "Країна" або "Місто"
      then: yup.string().required(t('dictionaries.countryName'))
    }),
    city: yup.string().when('type', {
      is: (val) => val === 2, // Якщо обрано тип "Місто"
      then: yup.string().required(t('dictionaries.enterNameCity'))
    }),
    projectType: yup.string().when('type', {
      is: (val) => val === 3, // Якщо обрано тип "Тип проекту"
      then: yup.string().required(t('dictionaries.enterType'))
    })
  });

  const open = popupData.visible;

  const {
    control,
    handleSubmit,
    reset,
    formState: {errors, isValid, isSubmitting}
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      type: '',
      country: '',
      city: '',
      projectType: ''
    }
  });

  useEffect(() => {
    if (open && popupData.type === 1) {
      reset({
        type: popupData.type,
        country: popupData.data.name,
        city: '',
        projectType: ''
      });
    } else if (open && popupData.type === 2) {
      reset({
        type: popupData.type,
        country: '',
        city: popupData.data.name,
        projectType: ''
      });
    } else if (open && popupData.type === 3) {
      reset({
        type: popupData.type,
        country: '',
        city: '',
        projectType: popupData.data.name
      });
    }
  }, [open]);

  const onSubmit = async (data) => {
    const formData = new FormData();

    if (popupData.type === 1) {
      formData.append('name', data.country);
      const res = await dispatch(editCountry(popupData.data.id, data.country));
      if (res.payload) {
        notifySuccess(t('all.successfully'));
        await dispatch(getCountries());
        closePopup();
      }
    }
    if (popupData.type === 2) {
      formData.append('name', data.city);
      const res = await dispatch(editCity(popupData.data.id, data.city));
      if (res.payload) {
        notifySuccess(t('all.successfully'));
        await dispatch(getCities());

        closePopup();
      }
    }
    if (popupData.type === 3) {
      formData.append('name', data.projectType);
      const res = await dispatch(editProjectType(popupData.data.id, data.projectType));
      if (res.payload) {
        notifySuccess(t('all.successfully'));
        await dispatch(getProjectTypes());
        closePopup();
      }
    }
  };

  return (
    <DialogMUI maxWidth={'sm'} open={open} title={t('dictionaries.editTitle')} onClose={closePopup}>
      {popupData.type === 1 && (
        <div>
          <div className='user-dictionaries__input'>
            <Controller
              name='country'
              control={control}
              render={({field}) => (
                <InputMUI
                  label={t('dictionaries.nameCountry')}
                  value={field.value}
                  fullWidth={true}
                  error={errors.country?.message}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[0-9]/g, '');
                    field.onChange(value);
                  }}
                  inputProps={{
                    ...field,
                    pattern: '[A-Za-zА-Яа-яЁёЇїІіЄєҐґ\\s]*'
                  }}
                  className={'no-icons'}
                />
              )}
            />
          </div>
        </div>
      )}
      {popupData.type === 2 && (
        <div>
          <div className='user-dictionaries__input'>
            <Controller
              name='city'
              control={control}
              render={({field}) => (
                <InputMUI
                  label={t('dictionaries.enterNameCity')}
                  value={field.value}
                  error={errors.city?.message}
                  fullWidth={true}
                  className={'no-icons'}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[0-9]/g, '');
                    field.onChange(value);
                  }}
                  inputProps={{
                    ...field,
                    pattern: '[A-Za-zА-Яа-яЁёЇїІіЄєҐґ\\s]*'
                  }}
                />
              )}
            />
          </div>
        </div>
      )}
      {popupData.type === 3 && (
        <div>
          <div className='user-dictionaries__input'>
            <Controller
              name='projectType'
              control={control}
              render={({field}) => (
                <InputMUI
                  label={t('dictionaries.type')}
                  onChange={field.onChange}
                  value={field.value}
                  error={errors.projectType?.message}
                  fullWidth={true}
                  className={'no-icons'}
                />
              )}
            />
          </div>
        </div>
      )}
      <div className={'user-dictionaries__submit'}>
        <ButtonMUI disabled={!isValid} loading={isSubmitting} type='submit' onClick={handleSubmit(onSubmit)}>
          {t('dictionaries.change')}
        </ButtonMUI>
      </div>
    </DialogMUI>
  );
};

export default UserDictionariesPopup;
